import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthFetch } from '../lib/useAuthFetch'
import { MarketSubNav } from '../components/shared/MarketSubNav'
import { Card, Pill, SectionLabel, DataTable } from '../components/ui'
import type { Column } from '../components/ui'

const API = import.meta.env.VITE_API_URL ?? ''

// ─── Types (mirror GET /api/v1/rivals/benchmarks) ────────────────────────────
interface Location {
  id: string
  name: string
}
interface CategoryBench {
  category: string
  your_avg: number
  market_avg: number
  market_low: number
  market_high: number
  gap: number
  position: 'pricier' | 'cheaper' | 'parity'
  comparable_skus: number
}
interface BenchmarkData {
  location: { id: string; name: string }
  own_menu: boolean
  tracked_rivals: number
  summary: {
    your_avg: number | null
    market_avg: number | null
    avg_gap: number | null
    categories_total: number
    categories_above: number
    categories_below: number
    comparable_skus_total: number
  }
  categories: CategoryBench[]
  previous?: {
    collected_at: string
    summary: BenchmarkData['summary']
    categories: CategoryBench[]
  } | null
}

// ─── Formatters (user-local) ─────────────────────────────────────────────────
const fmtMoney = (n: number) => `$${n.toFixed(2)}`
const fmtSignedMoney = (n: number) => `${n > 0 ? '+' : n < 0 ? '−' : ''}$${Math.abs(n).toFixed(2)}`
const fmtInt = (n: number) => n.toLocaleString('en-US')
const fmtPct = (n: number) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${Math.abs(n).toFixed(1)}%`

// Position → label + Pill tone. `pricier` (you charge more) is a competitive
// risk → danger; `cheaper` (you undercut) → accent; parity → neutral.
const POSITION_META: Record<CategoryBench['position'], { label: string; tone: 'danger' | 'accent' | 'neutral' }> = {
  pricier: { label: 'You price higher', tone: 'danger' },
  cheaper: { label: 'You price lower', tone: 'accent' },
  parity: { label: 'At market', tone: 'neutral' },
}
const gapColor = (gap: number) => (gap > 0 ? 'var(--danger)' : gap < 0 ? 'var(--accent)' : 'var(--text-3)')

// ─── Component ───────────────────────────────────────────────────────────────
export default function MyBenchmarks() {
  const authFetch = useAuthFetch()
  const navigate = useNavigate()

  const [locations, setLocations] = useState<Location[]>([])
  const [selectedLocation, setSelectedLocation] = useState<string>('')
  const [data, setData] = useState<BenchmarkData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [catFilter, setCatFilter] = useState<string>('All')
  const [locOpen, setLocOpen] = useState(false)
  const [catOpen, setCatOpen] = useState(false)

  // Load the org's locations, default to the first.
  useEffect(() => {
    authFetch(`${API}/api/v1/locations`)
      .then((r) => r.json())
      .then((d: { locations?: Location[] }) => {
        const locs = d.locations ?? []
        setLocations(locs)
        if (locs.length > 0) setSelectedLocation((cur) => cur || locs[0].id)
        else setLoading(false)
      })
      .catch(() => setError('Could not load your locations.'))
  }, [authFetch])

  const load = useCallback(() => {
    if (!selectedLocation) return
    setLoading(true); setError(null)
    authFetch(`${API}/api/v1/rivals/benchmarks?location_id=${selectedLocation}`)
      .then(async (r) => {
        if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || `HTTP ${r.status}`) }
        return r.json()
      })
      .then((d: { data: BenchmarkData }) => { setData(d.data); setCatFilter('All') })
      .catch((e) => setError(e?.message || 'Network error'))
      .finally(() => setLoading(false))
  }, [authFetch, selectedLocation])

  useEffect(() => { load() }, [load])

  const selectedName = useMemo(
    () => locations.find((l) => l.id === selectedLocation)?.name ?? data?.location.name ?? '—',
    [locations, selectedLocation, data]
  )

  const allCategories = data?.categories ?? []
  const sum = data?.summary
  const hasBenchmarks = allCategories.length > 0

  // Per-category derived signals. `pct` = how far your avg sits over/under the
  // market avg. `exposure` = |gap| × comparable SKUs — how far off × how much of
  // your menu it touches. Exposure is the single ranking key on this page: it
  // answers "where does this actually matter," so a $5 gap on 3 SKUs never
  // outranks a $1.40 gap spread across 130.
  const withDerived = useMemo(
    () => allCategories.map((c) => ({
      ...c,
      pct: c.market_avg > 0 ? (c.gap / c.market_avg) * 100 : 0,
      exposure: Math.abs(c.gap) * c.comparable_skus,
    })),
    [allCategories]
  )
  type Derived = (typeof withDerived)[number]

  const pricier = useMemo(
    () => withDerived.filter((c) => c.position === 'pricier').sort((a, b) => b.exposure - a.exposure),
    [withDerived]
  )
  const cheaper = useMemo(
    () => withDerived.filter((c) => c.position === 'cheaper').sort((a, b) => b.exposure - a.exposure),
    [withDerived]
  )
  const topExposure = pricier[0] ?? null
  const previousByCategory = useMemo(
    () => new Map((data?.previous?.categories ?? []).map((category) => [category.category, category])),
    [data]
  )
  const categoryGapChange = useCallback(
    (category: CategoryBench) => {
      const previous = previousByCategory.get(category.category)
      return previous ? Math.round((category.gap - previous.gap) * 100) / 100 : null
    },
    [previousByCategory]
  )

  // Decision order for the bars + table: priced-above first (biggest exposure),
  // then priced-below, then at-market (nothing to act on) sinks to the bottom.
  const orderedAll = useMemo(() => {
    const rank = (c: Derived) => (c.position === 'pricier' ? 0 : c.position === 'cheaper' ? 1 : 2)
    return [...withDerived].sort((a, b) => rank(a) - rank(b) || b.exposure - a.exposure)
  }, [withDerived])

  const shownCategories = useMemo(
    () => (catFilter === 'All' ? orderedAll : orderedAll.filter((c) => c.category === catFilter)),
    [orderedAll, catFilter]
  )

  // Overall verdict — leads with the MONEY (avg gap), never the category count,
  // so the one-glance headline can't contradict the dollar position the way the
  // old "you undercut in 5 of 12" line did while the avg gap was +$0.90 over.
  const verdict = useMemo(() => {
    if (!sum || sum.comparable_skus_total === 0 || sum.avg_gap == null || sum.market_avg == null) return null
    const gap = sum.avg_gap
    const pct = sum.market_avg > 0 ? (gap / sum.market_avg) * 100 : 0
    const absPct = Math.abs(pct)
    if (Math.abs(gap) < 0.5 || absPct < 2)
      return { title: 'Priced in line with the market', gap, pct, color: 'var(--text-2)' }
    if (gap > 0)
      return { title: absPct >= 8 ? 'Priced well above the market' : 'Priced above the market', gap, pct, color: 'var(--danger)' }
    return { title: absPct >= 8 ? 'Priced well below the market' : 'Priced below the market', gap, pct, color: 'var(--accent)' }
  }, [sum])

  const overallGapChange = sum?.avg_gap != null && data?.previous?.summary.avg_gap != null
    ? Math.round((sum.avg_gap - data.previous.summary.avg_gap) * 100) / 100
    : null
  const overallMovement = overallGapChange == null || !verdict
    ? 'New benchmark baseline'
    : Math.abs(overallGapChange) < 0.01
      ? 'Overall gap unchanged'
      : verdict.gap >= 0
        ? `Gap ${overallGapChange > 0 ? 'widened' : 'narrowed'} ${fmtSignedMoney(Math.abs(overallGapChange))} since the prior scan`
        : `Value lead ${overallGapChange < 0 ? 'widened' : 'narrowed'} ${fmtSignedMoney(Math.abs(overallGapChange))} since the prior scan`
  const topExposureChange = topExposure ? categoryGapChange(topExposure) : null
  const movementHeadline = !verdict || overallGapChange == null
    ? verdict?.title ?? 'Your market position'
    : Math.abs(overallGapChange) < 0.01
      ? 'Your market position held steady'
      : verdict.gap >= 0
        ? `Your price gap ${overallGapChange > 0 ? 'widened' : 'narrowed'}`
        : `Your value lead ${overallGapChange < 0 ? 'widened' : 'narrowed'}`

  // ── Detailed benchmark table ──────────────────────────────────────────────
  const columns: Column<CategoryBench>[] = [
    { key: 'category', header: 'Category', mono: false, render: (r) => <span style={{ fontWeight: 700, color: 'var(--text-1)' }}>{r.category}</span> },
    { key: 'your_avg', header: 'Your avg', align: 'right', render: (r) => <span style={{ fontWeight: 700, color: 'var(--text-1)' }}>{fmtMoney(r.your_avg)}</span> },
    { key: 'market_avg', header: 'Market avg', align: 'right', render: (r) => <span style={{ color: 'var(--text-2)' }}>{fmtMoney(r.market_avg)}</span> },
    { key: 'market_low', header: 'Market low', align: 'right', render: (r) => <span style={{ color: 'var(--accent)' }}>{fmtMoney(r.market_low)}</span> },
    { key: 'market_high', header: 'Market high', align: 'right', render: (r) => <span style={{ color: 'var(--danger)' }}>{fmtMoney(r.market_high)}</span> },
    {
      key: 'gap', header: 'Gap vs. avg', align: 'right',
      render: (r) => <span style={{ fontWeight: 700, color: gapColor(r.gap) }}>{fmtSignedMoney(r.gap)}</span>,
    },
    {
      key: 'change', header: 'Since prior', align: 'right',
      render: (r) => {
        const change = categoryGapChange(r)
        if (change == null) return <span style={{ color: 'var(--text-3)' }}>Baseline</span>
        if (Math.abs(change) < 0.01) return <span style={{ color: 'var(--text-3)' }}>—</span>
        return <span title="Change in your gap versus the market average" style={{ fontWeight: 700, color: change > 0 ? 'var(--danger)' : 'var(--accent)' }}>{fmtSignedMoney(change)}</span>
      },
    },
    {
      key: 'comparable_skus', header: 'Comparable SKUs', align: 'right',
      render: (r) => <span style={{ color: 'var(--text-2)' }}>{fmtInt(r.comparable_skus)}</span>,
    },
    {
      key: 'position', header: 'Position', mono: false, align: 'right',
      render: (r) => {
        const m = POSITION_META[r.position]
        return <Pill tone={m.tone} variant="chip">{m.label}</Pill>
      },
    },
  ]

  // Dropdown chrome (shared with s22 conventions).
  const ddBtn = (active: boolean): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', gap: 5, padding: '5px 11px', borderRadius: 'var(--r-sm)',
    fontSize: 11, fontWeight: 600, border: `1.5px solid ${active ? 'var(--accent)' : 'var(--border-2)'}`,
    background: active ? 'var(--accent)' : 'var(--surface)', color: active ? '#fff' : 'var(--text-1)',
    cursor: 'pointer', fontFamily: 'var(--sans)', whiteSpace: 'nowrap',
  })
  const ddPop: React.CSSProperties = {
    position: 'absolute', top: 'calc(100% + 5px)', left: 0, background: 'var(--surface)',
    border: '1px solid var(--border-2)', borderRadius: 'var(--r-sm)', boxShadow: '0 8px 32px rgba(30,60,80,0.14)',
    zIndex: 300, minWidth: 160, padding: 5, maxHeight: 280, overflowY: 'auto',
  }
  const ddOpt = (sel: boolean): React.CSSProperties => ({
    padding: '7px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
    color: sel ? 'var(--accent)' : 'var(--text-1)', fontWeight: sel ? 700 : 400, whiteSpace: 'nowrap',
  })

  const catOptions = ['All', ...allCategories.map((c) => c.category)]

  // One column of the exposure board — a ranked, scannable list of where you
  // sit off the market in one direction. `over` (priced above) = danger/red and
  // flags the #1 row "Act first"; below-market = accent/teal and reads as safe.
  const boardColumn = (over: boolean, rows: Derived[]) => {
    const color = over ? 'var(--danger)' : 'var(--accent)'
    const soft = over ? 'var(--danger-soft)' : 'var(--accent-soft)'
    return (
      <Card style={{ padding: 0, overflow: 'hidden', minWidth: 0 }}>
        <div style={{ padding: '11px 14px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8, background: soft }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-1)' }}>{over ? 'Priced above market' : 'Below market'}</span>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)' }}>{rows.length}</span>
          <span style={{ marginLeft: 'auto', fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase', color }}>
            {over ? 'Review first' : 'Holding value'}
          </span>
        </div>
        <div>
          {rows.length === 0 ? (
            <div style={{ padding: '14px', fontSize: 12, color: 'var(--text-2)', lineHeight: 1.5 }}>
              {over
                ? "Nothing runs above market — you're not the expensive option in any category."
                : "You're not undercutting the market in any category."}
            </div>
          ) : rows.slice(0, 5).map((c, i) => (
            <div key={c.category} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px',
              borderTop: i > 0 ? '1px solid var(--border)' : 'none',
              background: over && i === 0 ? soft : 'transparent',
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1)' }}>{c.category}</span>
                  {over && i === 0 && <Pill tone="danger" variant="chip">ACT FIRST</Pill>}
                </div>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', marginTop: 2 }}>
                  {fmtInt(c.comparable_skus)} comparable SKUs
                  {categoryGapChange(c) != null && Math.abs(categoryGapChange(c)!) >= 0.01
                    ? ` · ${fmtSignedMoney(categoryGapChange(c)!)} since prior`
                    : categoryGapChange(c) == null ? ' · new baseline' : ' · unchanged'}
                </div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 14, fontWeight: 700, color }}>{fmtSignedMoney(c.gap)}</div>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)' }}>{fmtPct(c.pct)} vs market</div>
              </div>
            </div>
          ))}
        </div>
      </Card>
    )
  }

  return (
    <div
      onClick={() => { setLocOpen(false); setCatOpen(false) }}
      style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--bg)', fontFamily: 'var(--sans)', color: 'var(--text-1)', fontSize: 14, overflow: 'hidden' }}
    >
      {/* TOPBAR */}
      <div style={{ padding: '12px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0, background: 'var(--surface)' }}>
        <div>
          <div style={{ fontSize: 17, fontWeight: 700, letterSpacing: '-0.02em' }}>My Benchmarks</div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', textTransform: 'uppercase' }}>
            Where you stand in the market
          </div>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          {data && hasBenchmarks && (
            <Pill tone="neutral" variant="chip" style={{ letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              {fmtInt(sum?.comparable_skus_total ?? 0)} comparable SKUs · {fmtInt(data.tracked_rivals)} tracked rivals
            </Pill>
          )}
        </div>
      </div>

      {/* MARKET NAV TABS */}
      <MarketSubNav />

      {/* FILTER BAR */}
      <div onClick={(e) => e.stopPropagation()} style={{ padding: '8px 28px', borderBottom: '1px solid var(--border)', background: 'var(--surface-2)', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        <span style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-3)' }}>Location</span>
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <div style={ddBtn(true)} onClick={(e) => { e.stopPropagation(); setLocOpen((o) => !o); setCatOpen(false) }}>
            {selectedName}
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg>
          </div>
          {locOpen && (
            <div style={ddPop}>
              {locations.length === 0
                ? <div style={{ padding: '7px 10px', fontSize: 12, color: 'var(--text-3)' }}>No locations yet</div>
                : locations.map((l) => (
                  <div key={l.id} style={ddOpt(l.id === selectedLocation)}
                    onClick={() => { setSelectedLocation(l.id); setLocOpen(false) }}>
                    {l.name}{l.id === selectedLocation ? ' ✓' : ''}
                  </div>
                ))}
            </div>
          )}
        </div>

        <div style={{ width: 1, height: 20, background: 'var(--border-2)', margin: '0 4px', flexShrink: 0 }} />
        <span style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-3)' }}>Category</span>
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <div data-testid="bench-cat-btn" style={ddBtn(catFilter !== 'All')} onClick={(e) => { e.stopPropagation(); setCatOpen((o) => !o); setLocOpen(false) }}>
            {catFilter}
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg>
          </div>
          {catOpen && (
            <div data-testid="bench-cat-pop" style={ddPop}>
              {catOptions.length <= 1
                ? <div style={{ padding: '7px 10px', fontSize: 12, color: 'var(--text-3)' }}>No categories yet</div>
                : catOptions.map((opt) => (
                  <div key={opt} style={ddOpt(opt === catFilter)}
                    onClick={() => { setCatFilter(opt); setCatOpen(false) }}>
                    {opt}{opt === catFilter ? ' ✓' : ''}
                  </div>
                ))}
            </div>
          )}
        </div>

        <div style={{ marginLeft: 'auto', fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-3)' }}>
          {data && hasBenchmarks ? `Benchmarked against ${fmtInt(data.tracked_rivals)} tracked rival${data.tracked_rivals === 1 ? '' : 's'} · ${selectedName}` : ''}
        </div>
      </div>

      {/* MAIN SCROLL */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 28px', minHeight: 0 }}>

        {/* VERDICT HERO — the one-glance answer: are we over- or under-priced? */}
        {!error && (loading || (data && hasBenchmarks)) && (
          <Card data-testid="bench-summary" style={{ padding: 0, overflow: 'hidden', marginBottom: 14, borderLeft: `3px solid ${verdict?.color ?? 'var(--border-2)'}` }}>
            {loading && !data ? (
              <div style={{ padding: '22px 22px', fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--text-3)' }}>Reading your position against the market…</div>
            ) : verdict && sum && data && (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: topExposure ? 'minmax(0, 1.65fr) minmax(300px, 0.75fr)' : '1fr' }}>
                  <div style={{ padding: '22px 24px', minWidth: 0, background: 'linear-gradient(115deg, var(--danger-soft), transparent 62%)' }}>
                    <SectionLabel style={{ marginBottom: 8 }}>What changed</SectionLabel>
                    <div style={{ fontSize: 29, fontWeight: 800, letterSpacing: '-0.025em', color: verdict.color, lineHeight: 1.08 }}>{movementHeadline}</div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginTop: 10 }}>
                      <span style={{ fontFamily: 'var(--mono)', fontSize: 34, fontWeight: 750, color: verdict.color, lineHeight: 1 }}>{fmtSignedMoney(verdict.gap)}</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1)' }}>average gap vs. market</span>
                      <Pill tone={overallGapChange != null && overallGapChange > 0 ? 'danger' : 'neutral'} variant="chip" style={{ padding: '3px 8px' }}>{overallMovement}</Pill>
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.55, marginTop: 11, maxWidth: 720 }}>
                      <b style={{ color: verdict.color }}>{verdict.title}</b> across {fmtInt(sum.comparable_skus_total)} comparable SKUs. You run above market in <b style={{ color: 'var(--danger)' }}>{sum.categories_above}</b> categor{sum.categories_above === 1 ? 'y' : 'ies'} and below in <b style={{ color: 'var(--accent)' }}>{sum.categories_below}</b>.
                    </div>
                  </div>

                  {topExposure && (
                    <div style={{ padding: '22px 22px', borderLeft: '1px solid var(--border-2)', background: 'var(--surface-2)', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <SectionLabel style={{ marginBottom: 8, color: 'var(--accent-hover)' }}>Recommended next step</SectionLabel>
                      <div style={{ fontSize: 19, fontWeight: 800, letterSpacing: '-0.015em', color: 'var(--text-1)' }}>Review {topExposure.category} first</div>
                      <div style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.5, marginTop: 7 }}>
                        {fmtInt(topExposure.comparable_skus)} SKUs average <b style={{ color: 'var(--danger)' }}>{fmtSignedMoney(topExposure.gap)} ({fmtPct(topExposure.pct)})</b> above market.
                        {topExposureChange != null && Math.abs(topExposureChange) >= 0.01 ? <> The gap moved <b style={{ color: topExposureChange > 0 ? 'var(--danger)' : 'var(--accent)' }}>{fmtSignedMoney(topExposureChange)}</b> since the prior scan.</> : ''}
                      </div>
                      <button
                        onClick={() => navigate(`/prices?view=priority&category=${encodeURIComponent(topExposure.category)}&location_id=${encodeURIComponent(selectedLocation)}`)}
                        style={{ width: '100%', marginTop: 14, padding: '10px 13px', borderRadius: 'var(--r-sm)', border: '1px solid var(--accent)', background: 'var(--accent)', color: '#fff', cursor: 'pointer', fontFamily: 'var(--sans)', fontSize: 12, fontWeight: 800, whiteSpace: 'nowrap', boxShadow: 'var(--cta-shadow)' }}
                      >
                        Review {fmtInt(topExposure.comparable_skus)} {topExposure.category} prices →
                      </button>
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 0, padding: '13px 22px', borderTop: '1px solid var(--border)', background: 'var(--surface)' }}>
                  {[
                    ['Your avg', sum.your_avg != null ? fmtMoney(sum.your_avg) : '—'],
                    ['Market avg', sum.market_avg != null ? fmtMoney(sum.market_avg) : '—'],
                    ['Comparable SKUs', fmtInt(sum.comparable_skus_total)],
                    ['Tracked rivals', fmtInt(data.tracked_rivals)],
                  ].map(([l, v], i) => (
                    <div key={l} style={{ display: 'flex', flexDirection: 'column', gap: 3, padding: '0 18px', borderRight: i < 3 ? '1px solid var(--border)' : 'none' }}>
                      <span style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-3)' }}>{l}</span>
                      <span style={{ fontFamily: 'var(--mono)', fontSize: 15, fontWeight: 700, color: 'var(--text-1)' }}>{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>
        )}

        {/* EXPOSURE BOARD — where you're too high (act first) vs safely low. */}
        {!error && data && hasBenchmarks && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))', gap: 12, marginBottom: 16 }}>
            {boardColumn(true, pricier)}
            {boardColumn(false, cheaper)}
          </div>
        )}

        {/* ERROR */}
        {error && (
          <Card variant="sm" style={{ padding: '18px 20px', borderColor: 'var(--danger-soft)' }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--danger)', marginBottom: 6 }}>Couldn't load your benchmarks.</div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', marginBottom: 12 }}>{error}</div>
            <button onClick={load} style={{ fontFamily: 'var(--sans)', fontSize: 12, fontWeight: 600, padding: '6px 14px', borderRadius: 'var(--r-sm)', border: '1px solid var(--accent)', background: 'var(--accent)', color: '#fff', cursor: 'pointer' }}>Retry</button>
          </Card>
        )}

        {/* HONEST DEGRADE — no own menu / nothing comparable yet */}
        {!error && !loading && data && !hasBenchmarks && (
          <Card variant="sm" style={{ padding: '18px 20px' }}>
            <SectionLabel style={{ marginBottom: 6 }}>No benchmarks yet</SectionLabel>
            <div style={{ fontSize: 13, color: 'var(--text-1)', fontWeight: 600, marginBottom: 6 }}>
              {!data.own_menu
                ? `Your own menu isn't in range yet for ${selectedName}.`
                : `Nothing to benchmark at ${selectedName} yet.`}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.5 }}>
              {!data.own_menu
                ? 'We haven’t collected your own menu for this location, so there’s nothing to price against the market. Once it’s in range, your category benchmarks appear here.'
                : data.tracked_rivals === 0
                  ? 'Add a rival to your tracking set at this location to benchmark your prices against theirs.'
                  : 'Your menu and your tracked rivals don’t carry the same products yet, so there are no like-for-like comparisons to benchmark.'}
            </div>
          </Card>
        )}

        {/* DETAILED BENCHMARK TABLE — the single deeper layer under the board. */}
        {!error && (loading || hasBenchmarks) && (
          <Card data-testid="bench-table" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '13px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--text-3)" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M9 9h6M9 12h6M9 15h6" /></svg>
              <span style={{ fontSize: 12, fontWeight: 700 }}>Detailed Benchmark</span>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', marginLeft: 'auto' }}>Every category · ranked by exposure</span>
            </div>
            <DataTable
              columns={columns}
              rows={shownCategories}
              rowKey={(r) => r.category}
              empty={loading
                ? <span style={{ fontFamily: 'var(--mono)', fontSize: 11 }}>Pulling your benchmarks…</span>
                : <span style={{ fontFamily: 'var(--mono)', fontSize: 11 }}>No data for this category.</span>}
            />
          </Card>
        )}
      </div>
    </div>
  )
}
